/**
 * Optimized Price Formatter
 * - Safely removes trailing zeros from prices
 * - Handles price ranges and "From" text
 * - Properly formats thousands and decimal separators
 * - Includes error handling and performance optimizations
 * - Browser compatibility improvements
 */

(function() {
  // Configuration
  const config = {
    selectors: [
      '.price', 
      '.price-item',
      '.product-price',
      '.price__sale',
      '.price__regular',
      '.cart-item__price',
      '.product-item__price',
      '.price-per-item',
      '[data-price]',
      '.unit-price',
      '.money',
      '.product-card__price',
      '.totals__total-value',
      '.product-option',
      '.sticky-atc-price'
    ],
    // Debounce delay for mutation observer (ms) - reduced for faster response
    debounceDelay: 50,
    // Attribute to mark processed elements
    processedAttribute: 'data-price-processed',
    // Flag to prevent processing own changes
    isProcessing: false
  };

  // Debounce function to limit how often a function can run
  function debounce(func, delay) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, delay);
    };
  }

  /**
   * Safely format a number string based on its value and currency
   * @param {string} numStr - The number string to format
   * @param {string} currency - The currency symbol
   * @return {string} - The formatted number
   */
  function formatNumberValue(numStr, currency) {
    try {
      // Ensure input is a string and trim whitespace
      numStr = String(numStr).trim();
      
      // DEBUG: Log for EUR currency - REMOVED
      
      // If empty or not a valid number pattern, return as is
      if (!numStr || !/^[\d.,]+$/.test(numStr)) {
        return numStr;
      }
      
      // Special handling for Bulgarian currency (лв or BGN)
      if (currency === 'лв' || currency === 'BGN') {
        // Remove trailing .00 or ,00
        if (numStr.match(/^\d+[.,]00$/)) {
          return numStr.replace(/[.,]00$/, '');
        }
        // Remove trailing zeros after comma (e.g., 120,00 -> 120)
        if (numStr.match(/,0+$/)) {
          return numStr.replace(/,0+$/, '');
        }
        // Remove trailing zeros after dot (e.g., 120.00 -> 120)
        if (numStr.match(/\.0+$/)) {
          return numStr.replace(/\.0+$/, '');
        }
        // If the number contains a space (likely a thousands separator in BG), return as is
        if (numStr.includes(' ')) {
          return numStr;
        }
      }
      
      // Case 1: Comma as decimal separator (e.g., "10,00")
      if (numStr.includes(',')) {
        // Remove trailing zeros after comma for EUR
        if (currency === 'EUR' || currency === '€') {
          // For EUR, just remove trailing zeros
          if (numStr.match(/,0+$/)) {
            return numStr.replace(/,0+$/, '');
          }
          return numStr;
        }
        // Remove trailing zeros after comma for other currencies
        if (numStr.match(/,0+$/)) {
          return numStr.replace(/,0+$/, '');
        }
        return numStr;
      }
      
      // Case 2: X.XXX format (period as thousands separator)
      if (numStr.match(/^\d+\.\d{3}$/)) {
        return numStr.replace('.', ' ');
      }
      
      // Case 3: X.X format (where X.X is not a whole thousand)
      if (numStr.match(/^\d+\.\d{1,2}$/)) {
        const num = parseFloat(numStr);
        // For EUR, just remove trailing zeros (don't add decimals)
        if (currency === 'EUR' || currency === '€') {
          // Remove trailing zeros for any amount (0.40 -> 0.4, 1.00 -> 1)
          if (numStr.match(/\.0+$/)) {
            return numStr.replace(/\.0+$/, '');
          }
          return numStr;
        }
        // For other currencies with small numbers, keep decimal format
        if (num < 100) {
          // Remove trailing zeros
          return numStr.replace(/\.0+$/, '');
        }
        // For numbers >= 100 with 2 decimal places like .00, treat as whole number
        if (numStr.match(/\.\d{2}$/)) {
          // If it ends with .00, it's likely a regular price, not abbreviated thousands
          if (numStr.match(/\.00$/)) {
            return numStr.replace(/\.00$/, '');
          }
          // If it has other decimals like .50, keep them
          return numStr.replace(/\.0+$/, '');
        }
        // For abbreviated thousands (e.g., 6.2 = 6 200) - only single digit after dot
        if (numStr.match(/^\d+\.\d{1}$/)) {
          return numStr.replace('.', ' ') + '00';
        }
        return numStr;
      }
      
      // Case 4: Remove trailing zeros from decimal
      if (numStr.includes('.')) {
        // For EUR, just remove trailing zeros (don't add decimals)
        if (currency === 'EUR' || currency === '€') {
          // Remove trailing zeros for any amount
          return numStr.replace(/\.0+$/, '');
        }
        // For other currencies, remove trailing zeros
        return numStr.replace(/\.0+$/, '');
      }
      
      // Case 5: Handle integer values for EUR
      // Check if this is an integer and should be formatted differently
      if (currency === 'EUR' || currency === '€') {
        const num = parseFloat(numStr);
        // If we have an integer value (no decimal part), return as is
        // This handles "1" -> "1" (not "1.00")
        if (Number.isInteger(num)) {
          return numStr;
        }
      }
      
      return numStr;
    } catch (error) {
      console.warn('Error in formatNumberValue:', error);
      return numStr; // Return original if error
    }
  }

  /**
   * Format a full price string (includes currency symbol, etc.)
   * @param {string} priceText - The price text to format
   * @return {string} - Formatted price text
   */
  function formatPriceText(priceText) {
    try {
      // If empty, return as is
      if (!priceText || typeof priceText !== 'string') {
        return priceText;
      }
      
      // Original price for fallback
      const originalText = priceText.trim();
      
      // Extract currency symbol (non-digit, non-space, non-punctuation)
      const currencyMatch = originalText.match(/[^\d\s,.]+/);
      const currency = currencyMatch ? currencyMatch[0] : '';
      
      // Handle "From" prices
      if (originalText.includes('From') || originalText.includes('от')) {
        // Extract all number sequences that could be prices
        const numberMatches = originalText.match(/[\d.,]+/g);
        
        if (!numberMatches) return originalText;
        
        // Format each number and replace in the string
        let result = originalText;
        numberMatches.forEach(num => {
          const formattedNum = formatNumberValue(num, currency);
          // Only replace exact matches to avoid partial replacements
          result = result.replace(new RegExp('\\b' + num + '\\b', 'g'), formattedNum);
        });
        return result;
      }
      
      // Handle price ranges with dash (e.g., "$10.00 - $20.00")
      if (originalText.includes('-') || originalText.includes('–')) {
        const parts = originalText.split(/[-–]/);
        
        if (parts.length === 2) {
          const formattedParts = parts.map(part => {
            const numberMatch = part.match(/[\d.,]+/);
            if (!numberMatch) return part.trim();
            
            const num = numberMatch[0];
            const formattedNum = formatNumberValue(num, currency);
            return part.trim().replace(num, formattedNum);
          });
          
          return formattedParts.join(' - ');
        }
      }
      
      // Standard single price
      const numericMatch = originalText.match(/[\d.,]+/);
      if (!numericMatch) return originalText;
      
      const numericPart = numericMatch[0];
      const formattedNum = formatNumberValue(numericPart, currency);
      
      // Replace only the exact number match
      return originalText.replace(new RegExp('\\b' + numericPart + '\\b'), formattedNum);
    } catch (error) {
      console.warn('Error in formatPriceText:', error);
      return priceText; // Return original if error
    }
  }

  /**
   * Process a single DOM element that contains a price
   * @param {Element} element - The element to process
   * @param {boolean} forceReprocess - Force reprocessing even if already marked
   */
  function processPriceElement(element, forceReprocess = false) {
    try {
      // Skip if already processed (unless forced)
      if (!forceReprocess && element.hasAttribute(config.processedAttribute)) {
        return;
      }
      
      let hasChanges = false;
      
      if (element.childElementCount === 0) {
        // Direct text node (simple price)
        const originalText = element.textContent;
        const formattedText = formatPriceText(originalText);
        
        if (originalText !== formattedText) {
          element.textContent = formattedText;
          hasChanges = true;
        }
      } else {
        // Process child text nodes
        const childTextNodes = Array.from(element.childNodes)
          .filter(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
          
        childTextNodes.forEach(node => {
          const originalText = node.textContent;
          const formattedText = formatPriceText(originalText);
          
          if (originalText !== formattedText) {
            node.textContent = formattedText;
            hasChanges = true;
          }
        });
        
        // Process money elements inside this container
        const nestedPriceElements = element.querySelectorAll('.money, [data-price]');
        nestedPriceElements.forEach(nested => {
          if (!nested.hasAttribute(config.processedAttribute)) {
            processPriceElement(nested, forceReprocess);
          }
        });
      }
      
      // Mark as processed only after successful processing
      if (hasChanges || forceReprocess) {
        element.setAttribute(config.processedAttribute, 'true');
      }
    } catch (error) {
      console.warn('Error processing price element:', error);
      // Don't throw - continue with other elements
    }
  }

  /**
   * Finds and processes all price elements on the page
   * @param {boolean} forceReprocess - Force reprocessing of all elements
   */
  function processPrices(forceReprocess = false) {
    // Prevent processing our own changes
    if (config.isProcessing) {
      return;
    }
    
    try {
      config.isProcessing = true;
      const elements = document.querySelectorAll(config.selectors.join(','));
      elements.forEach(el => processPriceElement(el, forceReprocess));
    } catch (error) {
      console.warn('Error in processPrices:', error);
    } finally {
      // Use setTimeout to ensure DOM updates complete before we allow new processing
      setTimeout(() => {
        config.isProcessing = false;
      }, 10);
    }
  }

  // Debounced version for use with MutationObserver
  const debouncedProcessPrices = debounce(processPrices, config.debounceDelay);

  // Function to initialize everything
  function initialize() {
    // Initial processing
    processPrices();
    
    // Setup observers for dynamic content
    if (typeof MutationObserver !== 'undefined') {
      try {
        const observer = new MutationObserver(mutations => {
          // Ignore mutations if we're currently processing
          if (config.isProcessing) {
            return;
          }
          
          let shouldProcess = false;
          let hasNewNodes = false;
          
          // Check if any relevant elements were added or attributes changed
          for (const mutation of mutations) {
            // Ignore our own attribute changes
            if (mutation.type === 'attributes' && 
                mutation.attributeName === config.processedAttribute) {
              continue;
            }
            
            // Check for new elements
            if (mutation.type === 'childList' && mutation.addedNodes.length) {
              for (const node of mutation.addedNodes) {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Check if this is a price element or contains price elements
                  if (config.selectors.some(selector => 
                      node.matches && node.matches(selector) || 
                      node.querySelector && node.querySelector(selector))) {
                    shouldProcess = true;
                    hasNewNodes = true;
                    break;
                  }
                }
              }
              if (shouldProcess) break;
            }
          }
          
          if (shouldProcess) {
            // For new nodes, process immediately without debounce for faster response
            if (hasNewNodes) {
              processPrices();
            } else {
              debouncedProcessPrices();
            }
          }
        });
        
        // Only observe relevant parts of the page where prices typically appear
        const containers = [
          document.querySelector('.page-container'),
          document.querySelector('main'),
          document.querySelector('#shopify-section-header'),
          document.querySelector('#cart-drawer')
        ].filter(Boolean);
        
        // If no specific containers found, observe body as fallback
        if (containers.length === 0) {
          observer.observe(document.body, { 
            childList: true, 
            subtree: true 
          });
        } else {
          // Otherwise observe only the relevant containers
          containers.forEach(container => {
            observer.observe(container, {
              childList: true,
              subtree: true
            });
          });
        }
      } catch (error) {
        console.warn('Error setting up MutationObserver:', error);
      }
    }
  }
  
  // Expose globally so other scripts can trigger reprocessing
  window.rdnlProcessPrices = function(forceReprocess) {
    processPrices(forceReprocess === true);
  };

  // Event listeners
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
  
  // Handle Shopify section updates - these need immediate processing
  document.addEventListener('shopify:section:load', () => {
    // Clear processed flags for reprocessing after section updates
    setTimeout(() => {
      const processedElements = document.querySelectorAll(`[${config.processedAttribute}]`);
      processedElements.forEach(el => el.removeAttribute(config.processedAttribute));
      processPrices();
    }, 10);
  });
  
  document.addEventListener('shopify:section:select', () => {
    setTimeout(() => processPrices(), 10);
  });
  
  document.addEventListener('cart:refresh', () => {
    setTimeout(() => processPrices(), 10);
  });
})(); 
